import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Link,
  Divider,
} from "@mui/material";
import {
  Book as GuideIcon,
  VideoLibrary as VideoIcon,
  Article as BlogIcon,
  Download as DownloadIcon,
  PlayCircleOutline as WebinarIcon,
} from "@mui/icons-material";
import Layout from "../components/layout/Calayout";

const ClientResources = () => {
  // Resources data
  const clientResources = {
    taxGuides: [
      {
        id: 1,
        title: "Small Business Tax Deduction Guide",
        description: "Comprehensive guide to maximize your tax deductions",
        type: "PDF Guide",
        fileSize: "2.5 MB",
        link: "/resources/tax-deductions-guide.pdf",
      },
      {
        id: 2,
        title: "Quarterly Tax Filing Checklist",
        description:
          "Step-by-step checklist for timely and accurate tax filing",
        type: "Downloadable Checklist",
        fileSize: "350 KB",
        link: "/resources/tax-filing-checklist.pdf",
      },
      {
        id: 3,
        title: "Expense Tracking Workbook",
        description: "Excel template to track and categorize business expenses",
        type: "Excel Template",
        fileSize: "1.2 MB",
        link: "/resources/expense-tracking-workbook.xlsx",
      },
    ],
    webinars: [
      {
        id: 1,
        title: "Understanding Business Tax Strategies",
        description: "Expert insights on optimizing your tax planning",
        date: "2024-05-15",
        time: "2:00 PM EST",
        link: "/webinars/tax-strategies",
      },
      {
        id: 2,
        title: "Financial Planning for Small Businesses",
        description: "Strategies for financial growth and stability",
        date: "2024-06-20",
        time: "3:30 PM EST",
        link: "/webinars/financial-planning",
      },
      {
        id: 3,
        title: "Navigating Tax Law Changes",
        description: "Latest updates and implications for your business",
        date: "2024-07-10",
        time: "1:00 PM EST",
        link: "/webinars/tax-law-changes",
      },
    ],
    blogPosts: [
      {
        id: 1,
        title: "Top 10 Tax Saving Strategies for Small Businesses",
        description: "Practical tips to reduce your tax liability",
        date: "2024-03-01",
        readTime: "5 min read",
        link: "/blog/tax-saving-strategies",
      },
      {
        id: 2,
        title: "Navigating Tax Changes in 2024",
        description: "Key tax regulations every business owner should know",
        date: "2024-01-15",
        readTime: "7 min read",
        link: "/blog/tax-changes-2024",
      },
      {
        id: 3,
        title: "Financial Forecasting for Small Businesses",
        description: "Tools and techniques for accurate financial planning",
        date: "2024-04-22",
        readTime: "6 min read",
        link: "/blog/financial-forecasting",
      },
    ],
  };

  const ResourceSection = ({ title, resources, icon: Icon, renderItem }) => (
    <Card sx={{ marginBottom: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" marginBottom={2}>
          <Avatar sx={{ marginRight: 2, backgroundColor: "primary.main" }}>
            <Icon />
          </Avatar>
          <Typography variant="h6">{title}</Typography>
        </Box>
        <List>
          {resources.map((resource, index) => (
            <React.Fragment key={resource.id}>
              {renderItem(resource)}
              {index < resources.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <Box sx={{ flexGrow: 1, padding: 3, backgroundColor: "#F5F5F5" }}>
        <Typography variant="h4" color="primary" gutterBottom>
          Client Resources
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Valuable resources to support your business financial management
        </Typography>

        <Grid container spacing={3} sx={{ marginTop: 2 }}>
          <Grid item xs={12} md={4}>
            <ResourceSection
              title="Tax Guides & Checklists"
              resources={clientResources.taxGuides}
              icon={GuideIcon}
              renderItem={(guide) => (
                <ListItem
                  button
                  component={Link}
                  href={guide.link}
                  target="_blank"
                >
                  <ListItemAvatar>
                    <Avatar>
                      <DownloadIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={guide.title}
                    secondary={`${guide.type} (${guide.fileSize})`}
                    primaryTypographyProps={{ fontWeight: "bold" }}
                  />
                </ListItem>
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ResourceSection
              title="Upcoming Webinars"
              resources={clientResources.webinars}
              icon={VideoIcon}
              renderItem={(webinar) => (
                <ListItem button component={Link} href={webinar.link}>
                  <ListItemAvatar>
                    <Avatar>
                      <WebinarIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={webinar.title}
                    secondary={`${webinar.date} at ${webinar.time}`}
                    primaryTypographyProps={{ fontWeight: "bold" }}
                  />
                </ListItem>
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ResourceSection
              title="Blog Posts"
              resources={clientResources.blogPosts}
              icon={BlogIcon}
              renderItem={(post) => (
                <ListItem
                  button
                  component={Link}
                  href={post.link}
                  target="_blank"
                >
                  <ListItemAvatar>
                    <Avatar>
                      <BlogIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={post.title}
                    secondary={`Published: ${post.date} (${post.readTime})`}
                    primaryTypographyProps={{ fontWeight: "bold" }}
                  />
                </ListItem>
              )}
            />
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
};

export default ClientResources;
